import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import type { OuNode } from './ouTree';
import { useOuExpansion } from './useOuExpansion';
import { useOuSelection } from './useOuSelection';
import './OuTreeSelect.css';

export interface OuTreeSelectProps {
  items: OUItem[];
  mode: 'single' | 'multi';
  value: string[];
  onChange: (ids: string[]) => void;
  variant?: 'inline' | 'dropdown';
  placeholder?: string;
  isDisabled?: boolean;
}

function findNodeLabel(nodes: OuNode[], nodeId: string): string | null {
  for (const node of nodes) {
    if (node.ou.id === nodeId) return node.ou.name;
    const childLabel = findNodeLabel(node.children, nodeId);
    if (childLabel) return childLabel;
  }
  return null;
}

export function OuTreeSelect({ items, mode, value, onChange, variant = 'inline', placeholder, isDisabled }: OuTreeSelectProps) {
  const { t } = useTranslation();
  const { tree, isCollapsed, toggle: toggleExpansion } = useOuExpansion(items);
  const { getState, toggle: toggleMultiSelection } = useOuSelection(items, value, onChange);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  function handleNodeInputChange(nodeId: string): void {
    if (isDisabled) return;
    if (mode === 'multi') {
      toggleMultiSelection(nodeId);
      return;
    }
    onChange(value[0] === nodeId ? [] : [nodeId]);
    if (variant === 'dropdown') setDropdownOpen(false);
  }

  function renderNode(node: OuNode, depth: number): ReactNode {
    const hasChildren = node.children.length > 0;
    const collapsed = hasChildren && isCollapsed(node.ou.id);
    const checkState = mode === 'multi' ? getState(node.ou.id) : (value[0] === node.ou.id ? 'checked' : 'unchecked');

    return (
      <li key={node.ou.id} className="ou-tree-select-item">
        <div className="ou-tree-select-row" style={{ paddingLeft: depth * 16 }}>
          {hasChildren ? (
            <button
              type="button"
              className={`ou-tree-select-chevron${collapsed ? '' : ' ou-tree-select-chevron--open'}`}
              onClick={() => toggleExpansion(node.ou.id)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? t('admin.ouExpand') : t('admin.ouCollapse')}
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          ) : (
            <span className="ou-tree-select-spacer" />
          )}

          <label className="ou-tree-select-label">
            <input
              type={mode === 'multi' ? 'checkbox' : 'radio'}
              checked={checkState === 'checked'}
              ref={inputElement => {
                if (inputElement) inputElement.indeterminate = checkState === 'indeterminate';
              }}
              disabled={isDisabled}
              onChange={() => handleNodeInputChange(node.ou.id)}
              aria-label={node.ou.name}
            />
            <span>{node.ou.name}</span>
          </label>
        </div>
        {hasChildren && !collapsed && (
          <ul className="ou-tree-select-children">
            {node.children.map(childNode => renderNode(childNode, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  const treeMarkup = <ul className="ou-tree-select-list">{tree.map(rootNode => renderNode(rootNode, 0))}</ul>;

  if (variant === 'inline') {
    return <div className="ou-tree-select ou-tree-select--inline">{treeMarkup}</div>;
  }

  const selectedLabel = value[0] ? (findNodeLabel(tree, value[0]) ?? value[0]) : (placeholder ?? t('admin.ouSelectPlaceholder'));

  return (
    <div className="ou-tree-select ou-tree-select--dropdown">
      <button
        type="button"
        className="ou-tree-select-trigger"
        onClick={() => setDropdownOpen(previousOpen => !previousOpen)}
        disabled={isDisabled}
      >
        <span>{selectedLabel}</span>
        <i className={`fas fa-chevron-down${dropdownOpen ? ' ou-tree-select-trigger-icon--open' : ''}`} aria-hidden="true" />
      </button>
      {dropdownOpen && (
        <>
          <div className="ou-tree-select-scrim" onClick={() => setDropdownOpen(false)} />
          <div className="ou-tree-select-popover">{treeMarkup}</div>
        </>
      )}
    </div>
  );
}
